import { Divider, Paper, Tab, Tabs, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { includes } from "lodash";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import CustomDataGridFilterToolbar from "../../../common/js/components/CustomDataGridFilterToolbar";
import StatusChip from "../../../common/js/components/StatusChip";
import OutsideCompModal from "../comp/OutsideCompModal";
import { buildAddress, formatDate } from "../utils/operations";
import BrokerDealsGrid from "./BrokerDealsGrid";
import dealsTables from "./dealsTables";

const StyledPageToolbar = styled("div")({
    marginBottom: "20px",
    marginTop: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
});

const StyledDivider = styled(Divider)({
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    border: "none",
    height: "1px",
});

const StyledTabs = styled(Tabs)({
    borderBottom: "none",
    marginRight: "20px",
    position: "absolute",
    bottom: 0,
    width: "50em",
});

const StyledTab = styled(Tab)({
    borderBottom: "none",
    marginRight: "20px",
});

const StyledPaper = styled(Paper)({
    padding: "10px 20px",
    fontSize: 14,
    marginBottom: "15px",
});

const StyledTabsAndFilterContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "16px",
});

const StyledTabsWithFilterContainer = styled("div")({
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "16px",
});

const StyledTabsContainer = styled("div")({
    position: "relative",
    minHeight: "65px",
});

const StyledReviewLink = styled("span")({
    "&:hover": {
        cursor: "pointer",
        color: "#2C99D2",
    },
    color: "#595959",
    textDecoration: "underline",
});

const StyledUndoSpan = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#595959",
    fontSize: "80%",
    marginLeft: "3px",
});

const StyledLink = styled(RouterLink)({
    textDecoration: "none",
    lineHeight: "1.2",
});

const StyledDealName = styled("span")(({ isTerminated }) => ({
    color: isTerminated ? "#D63636" : "#2C99D2",
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    fontWeight: 600,
    wordWrap: "break-word",
    whiteSpace: "normal",
    lineHeight: "1.3",
}));

const StyledDealCell = styled("div")({
    display: "flex",
    flexDirection: "column",
});

const StyledDataGridFilter = styled("div")({
    display: "flex",
    width: "70%",
    padding: "2px",
});

const StyledBrokerFilter = styled("div")({
    display: "flex-end",
    padding: "2px",
});

const StyledReviewedByDiv = styled("div")({
    fontWeight: 600,
    marginRight: '2px'
});

const BrokerDeals = (props) => {
    const {
        addNewDealFromOutsideCompModal,
        deals,
        dealView,
        errors,
        role,
        searchProperty,
        switchDealView,
        toggleReviewedBy,
        getDeals,
        setDealsPage,
        setDealsFilter,
        user,
    } = props;

    const brokerDeals = deals[dealView];

    const components = {
        StyledDealCell,
        StyledLink,
        StyledDealName,
        StyledReviewLink,
        StyledUndoSpan,
        StatusChip,
        StyledReviewedByDiv
    };

    const updateReviewStatus = async (processed, dealKey, tab) => {
        await toggleReviewedBy(processed, dealKey, tab);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await getDeals(url, dealView);
    };

    const renderBrokers = (brokers) => {
        if (!brokers) return "";
        const brokerNames = brokers.split(",");
        if (brokerNames.length > 1) {
            return (
                <Tooltip title={brokers} placement="top">
                    <span>{`${brokerNames.length} Brokers`}</span>
                </Tooltip>
            );
        }
        return brokers;
    };

    const isTerminated = (dealStatus) => {
        return dealStatus === "Portland Terminated" || dealStatus === "AZ Terminated";
    };

    const getDealStatus = (dealStatus) => {
        if (dealStatus === null) {
            return "";
        } else if (
            dealStatus === "Portland Termination Review Required" ||
            dealStatus === "AZ Termination Review Required"
        ) {
            return "Termination Review Required";
        } else if (dealStatus === "Portland Terminated" || dealStatus === "AZ Terminated") {
            return "Terminated";
        }
        return dealStatus;
    };

    const functions = { buildAddress, formatDate, renderBrokers, updateReviewStatus, getDealStatus, isTerminated };

    const config = dealsTables.getDealsViewConfig(user, components, functions, props)[dealView];
    const url = config.url;
    const columns = config.columns;
    const filterColumns = config.filterColumns;

    const getDealsData = () => {
        getDeals(url, dealView);
    };

    return (
        <React.Fragment>
            <StyledPageToolbar>
                <div>
                    <h2 className="page-title">Deals</h2>
                </div>
                <div>
                    <OutsideCompModal
                        {...props}
                        errors={errors}
                        searchFunction={searchProperty}
                        addOutsideComp={addNewDealFromOutsideCompModal}
                        internal={true}
                    />
                </div>
            </StyledPageToolbar>

            <StyledPaper>
                {includes(["broker", "evp", "debtEquity"], role) ? (
                    <StyledTabsWithFilterContainer>
                        <StyledTabsContainer>
                            <StyledTabs value={dealView} onChange={switchDealView}>
                                <StyledTab value="broker_open" label="Open Deals" />
                                <StyledTab value="broker_closed" label="Closed Deals" />
                            </StyledTabs>
                        </StyledTabsContainer>
                        <StyledBrokerFilter>
                            <CustomDataGridFilterToolbar
                                key={dealView}
                                fields={filterColumns}
                                setFilter={setDealsFilter}
                                setPage={setDealsPage}
                                onSubmit={getDealsData}
                            />
                        </StyledBrokerFilter>
                    </StyledTabsWithFilterContainer>
                ) : (
                    <StyledTabsAndFilterContainer>
                        <StyledTabsContainer>
                            {includes(["portlandBrokerAdmin"], role) && (
                                <StyledTabs value={dealView} onChange={switchDealView}>
                                    <StyledTab value="broker_open" label="My Open Deals" />
                                    <StyledTab value="broker_closed" label="My Closed Deals" />
                                    <StyledTab value="portland_broker_admin_sale" label="Draft Oregon Sale Deals" />
                                    <StyledTab value="portland_broker_admin" label="Oregon Deal Review" />
                                    <StyledTab value="portland_broker_admin_closed" label="Oregon Closed Deals" />
                                </StyledTabs>
                            )}
                            {includes(["arizonaBrokerAdmin"], role) && (
                                <StyledTabs value={dealView} onChange={switchDealView}>
                                    <StyledTab value="broker_open" label="My Open Deals" />
                                    <StyledTab value="broker_closed" label="My Closed Deals" />
                                    <StyledTab value="arizona_broker_admin_review" label="Arizona Deal Review" />
                                    <StyledTab value="arizona_broker_admin_closed" label="Arizona Closed Deals" />
                                </StyledTabs>
                            )}
                        </StyledTabsContainer>
                        <StyledDataGridFilter>
                            <CustomDataGridFilterToolbar
                                key={dealView}
                                fields={filterColumns}
                                setFilter={setDealsFilter}
                                setPage={setDealsPage}
                                onSubmit={getDealsData}
                            />
                        </StyledDataGridFilter>
                    </StyledTabsAndFilterContainer>
                )}
                <StyledDivider />
                <BrokerDealsGrid
                    key={dealView}
                    brokerDeals={brokerDeals}
                    columns={columns}
                    getDealsData={getDealsData}
                    {...props}
                ></BrokerDealsGrid>
            </StyledPaper>
        </React.Fragment>
    );
};

export default BrokerDeals;
